import * as z from "zod";
import { userFacingError } from "../authorization";
import { isUniqueConstraintViolation } from "../dbErrors";
import { zql } from "../generatedDrizzleSchema";
import { BUILTIN_ROLE_IDS, type BuiltinRoleId } from "../rbac/roles";
import { assertRoleDoesNotGrant } from "../rbac/validation/grantContent";
import { USER_ASSIGNABLE_SPACE_MODES } from "../spaceMode";
import {
  assertNameAvailable,
  assertNotPersonal,
  assertSlugAvailable,
  spaceNameField,
  spaceSlugField,
} from "../spaceName";
import { mutator } from "./mutator";

export const updateSpace = mutator({
  schema: z.object({
    spaceId: z.string(),
    name: spaceNameField.optional(),
    slug: spaceSlugField.optional(),
    mode: z.enum(USER_ASSIGNABLE_SPACE_MODES).optional(),
    defaultJoinBuiltinRoleId: z.enum(BUILTIN_ROLE_IDS).nullable().optional(),
    defaultJoinCustomRoleId: z.string().nullable().optional(),
  }),
  resolve: {
    space: { kind: "space", from: "spaceId" },
  },
  permissions: [
    {
      permission: "space:manage",
      scopeId: ({ args }) => args.space.id,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      name,
      slug,
      mode,
      defaultJoinBuiltinRoleId,
      defaultJoinCustomRoleId,
      space,
    },
  }) => {
    assertNotPersonal(space, "update");

    if (name !== undefined || slug !== undefined) {
      const existing = await tx.run(zql.spaces);
      if (name !== undefined) assertNameAvailable(existing, name, space.id);
      if (slug !== undefined) assertSlugAvailable(existing, slug, space.id);
    }

    if (slug !== undefined && slug !== space.slug) {
      ctx.recordAuditMetadata?.({ slug: { from: space.slug, to: slug } });
    }

    if (
      defaultJoinBuiltinRoleId !== undefined ||
      defaultJoinCustomRoleId !== undefined
    ) {
      const effectiveBuiltin: BuiltinRoleId | null =
        defaultJoinBuiltinRoleId !== undefined
          ? defaultJoinBuiltinRoleId
          : ((space.defaultJoinBuiltinRoleId as BuiltinRoleId | null) ?? null);
      const effectiveCustom: string | null =
        defaultJoinCustomRoleId !== undefined
          ? defaultJoinCustomRoleId
          : (space.defaultJoinCustomRoleId ?? null);

      if (effectiveBuiltin && effectiveCustom) {
        throw userFacingError(
          "A space can have only one default join role at a time"
        );
      }

      if (effectiveBuiltin) {
        await assertRoleDoesNotGrant(
          tx,
          { builtinRoleId: effectiveBuiltin },
          "space:manage"
        );
      }
      if (effectiveCustom) {
        await assertRoleDoesNotGrant(
          tx,
          { customRoleId: effectiveCustom },
          "space:manage"
        );
      }
    }

    try {
      await tx.mutate.spaces.update({
        id: space.id,
        ...(name !== undefined && { name }),
        ...(slug !== undefined && { slug }),
        ...(mode !== undefined && { mode }),
        ...(defaultJoinBuiltinRoleId !== undefined && {
          defaultJoinBuiltinRoleId,
        }),
        ...(defaultJoinCustomRoleId !== undefined && {
          defaultJoinCustomRoleId,
        }),
      });
    } catch (err) {
      if (
        name !== undefined &&
        isUniqueConstraintViolation(err, "spaces_tenant_name_unique")
      ) {
        throw userFacingError("A space with this name already exists");
      }
      if (
        slug !== undefined &&
        isUniqueConstraintViolation(err, "spaces_tenant_slug_unique")
      ) {
        throw userFacingError("This space URL is already taken");
      }
      throw err;
    }
  },
});
